import { FileText, ShieldCheck, Calculator, ClipboardList, Users, MessagesSquare } from "lucide-react";
import { Link } from "react-router-dom";
import { PageHero, SectionHeading, DisclaimerBar } from "../components/shared/Primitives";

const FEATURES = [
  { icon: Users, title: "Guidance for arranging care", body: "Step-by-step for setting up a safe, sensible independent arrangement." },
  { icon: FileText, title: "Care Booking Agreement", body: "A clear, fair agreement you and your carer can sign, today." },
  { icon: ShieldCheck, title: "Responsible Payer Confirmation", body: "Make it clear who pays — crucial in multi-family arrangements." },
  { icon: FileText, title: "Deposit & Payment Agreement", body: "Deposits, payment terms, late-payment handling — written down so there's no confusion." },
  { icon: ClipboardList, title: "Care Schedule & Expectations", body: "Shared document so everyone's clear on what happens and when." },
  { icon: Calculator, title: "Care job specification guidance", body: "Guidance to help families describe the care needed clearly, so expectations, tasks, hours, and responsibilities are easier to understand from the start." },
];

const JOIN_URL = "/membership/family-circle";

export default function FamilyAccess() {
  return (
    <div data-testid="page-family-access">
      <PageHero
        eyebrow="Family Care Office"
        title="Everything you need to arrange independent care with clarity and confidence."
        description="Family Care Office gives you practical guidance, templates and resources to help you arrange independent care with more clarity and confidence."
        accent="teal"
        bgTint="family"
      >
        <a
          href={JOIN_URL}
          className="btn-teal"
          data-testid="family-cta-access"
        >
          Explore Family Care Office
        </a>
        <a href="#features" className="btn-outline" data-testid="family-cta-see-whats-included">See what&rsquo;s included</a>
      </PageHero>

      <section id="features" className="section">
        <div className="container-jcc">
          <SectionHeading eyebrow="What's included" accent="teal" title="All the structure. None of the guesswork." />
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((f) => (
              <div key={f.title} className="card-base p-6" data-testid={`family-feature-${f.title.toLowerCase().split(' ').slice(0,2).join('-')}`}>
                <div className="w-11 h-11 rounded-xl bg-teal-50 text-teal flex items-center justify-center mb-4">
                  <f.icon className="w-5 h-5" strokeWidth={1.75} />
                </div>
                <h3 className="font-display font-semibold text-lg">{f.title}</h3>
                <p className="mt-2 text-ink-muted text-sm leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section" data-testid="family-care-discussion-room-section">
        <div className="container-jcc">
          <div
            className="rounded-3xl border-2 border-teal/60 bg-teal-50/30 p-8 md:p-12 grid lg:grid-cols-5 gap-8 items-center"
            data-testid="family-care-discussion-room-card"
          >
            <div className="lg:col-span-3">
              <div className="overline text-teal mb-3">Care Discussion Room / Care Partnership Room</div>
              <h2 className="font-display font-bold text-2xl md:text-3xl leading-tight">
                A private shared space for a family and a self-employed carer, before you commit.
              </h2>
              <p className="mt-4 text-ink-muted leading-relaxed">
                The Care Discussion Room is a private, pre-discussion space where a family
                and a prospective carer can talk through the arrangement together — expectations,
                schedule, boundaries, payment terms, key documents — before signing anything or
                activating the full Care Partnership Room. Just Care Community is not an agency
                and takes no commission from the arrangement; the room simply gives both sides
                a calm, organised place to reach a shared understanding.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to="/care-discussion-room"
                  className="btn-teal"
                  data-testid="family-cta-care-discussion-room"
                >
                  Learn about the Care Discussion Room
                </Link>
              </div>
            </div>
            <div className="lg:col-span-2">
              <div className="rounded-2xl bg-white border border-teal/30 p-6">
                <div className="w-11 h-11 rounded-xl bg-teal-50 text-teal flex items-center justify-center mb-4">
                  <MessagesSquare className="w-5 h-5" strokeWidth={1.75} />
                </div>
                <ul className="space-y-3 text-sm text-ink-muted leading-relaxed">
                  <li>&bull; Private to you and your prospective carer</li>
                  <li>&bull; A pre-discussion place before any commitment</li>
                  <li>&bull; Shared record of what you both agreed</li>
                  <li>&bull; Activate the full Care Partnership Room only when both sides are ready</li>
                  <li>&bull; JCC is not an agency and takes no commission</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section bg-surface-family">
        <div className="container-jcc grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <SectionHeading
              eyebrow="Membership"
              accent="teal"
              title="Simple monthly membership. Cancel any time."
              description="Family Care Office is a low-cost monthly membership designed to give families practical support while arranging independent care."
            />
            <div className="mt-6"><DisclaimerBar /></div>
          </div>
          <div className="rounded-3xl border-2 border-teal p-10 bg-white" data-testid="family-circle-card">
            <div className="overline text-teal">Family Care Office</div>
            <div className="mt-3 flex items-end gap-1">
              <span className="text-5xl font-bold">£19</span><span className="text-ink-muted mb-1.5">per month</span>
            </div>
            <p className="mt-6 text-sm text-ink-muted leading-relaxed">
              Practical guidance, templates and resources to help families arrange independent care with more clarity and confidence. Cancel any time.
            </p>
            <a
              href={JOIN_URL}
              className="btn-teal mt-8 w-full"
              data-testid="family-cta-join"
            >
              Join Family Care Office
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
